import { Component, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from '@layout/components/desktop/header/header.component';
import { FooterComponent } from '@layout/components/desktop/footer/footer.component';
import { StepersComponent } from '@shared/components/stepers/stepers.component';
import { filter } from 'rxjs';
import { IdleService } from '@services/idle.service';
import { InactiveMessageComponent } from '@shared/components/inactive-message/inactive-message.component';
import {
  ErrorStateService,
  LoadingStateService,
} from '@services/error.service';
import { ErrorMessageComponent } from '@shared/components/error-message/error-message.component';
import { LottiePlaneComponent } from '@app/shared/components/lottie-plane/lottie-plane.component';
import { isApprovedMercadoPagoReturn } from '@utils/paymentReturnUrl';
import { PaymentFlowService } from '@services/payment-flow.service';
import { buildPaymentSteps, Step } from '@utils/paymentSteps';

export type { Step };

/** Splash de arranque de la app: tapa la pantalla hasta que vence. */
const SPLASH_MS = 5000;

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    RouterOutlet,
    StepersComponent,
    InactiveMessageComponent,
    ErrorMessageComponent,
    LottiePlaneComponent,
    AsyncPipe,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css',
})
export class LayoutComponent implements OnInit {
  isLoading = true;
  isError = false;
  isInactive = false;
  currentStepIndex = 1;
  isParams: boolean = true;
  isMobile = false;

  listSteps: Step[] = [];

  private bookingInternalStep = 1;

  constructor(
    private router: Router,
    private idleService: IdleService,
    public errorState: ErrorStateService,
    public loadingState: LoadingStateService,
    private paymentFlow: PaymentFlowService
  ) {
    this.idleService.initListener();
    this.idleService.isInactive$.subscribe(value => {
      this.isInactive = value;
    });
  }

  ngOnInit(): void {
    this.checkWindowWidth();

    this.buildSteps();

    // El splash se saltea en la vuelta de MP con pago aprobado: esa pantalla
    // ya tiene su propio loader con mensajes de progreso, y cada segundo acá
    // es un segundo que el retrieve todavía no arrancó.
    //
    // Se escribe SIEMPRE por el servicio, nunca sobre this.isLoading: el
    // BehaviorSubject es la única fuente de verdad y el subscribe de abajo lo
    // baja al template, corra antes o después de esta línea.
    if (
      isApprovedMercadoPagoReturn(
        window.location.pathname + window.location.search
      )
    ) {
      this.loadingState.setLoading(false);
    } else {
      setTimeout(() => this.loadingState.setLoading(false), SPLASH_MS);
    }
    this.refreshIsParams();
    const savedStep = sessionStorage.getItem('currentStep');
    if (savedStep && !isNaN(+savedStep)) {
      this.bookingInternalStep = +savedStep;
    }
    this.errorState.isError$.subscribe(value => {
      this.isError = value;
    });

    this.loadingState.isLoading$.subscribe(value => {
      this.isLoading = value;
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.refreshIsParams();
        this.buildSteps();
        this.updateStepsByUrl(event.urlAfterRedirects);
      });

    const currentUrl = this.router.url;
    this.updateStepsByUrl(currentUrl);
  }

  /**
   * El stepper solo se renderiza con branding de agencia en sesión. Se
   * recalcula en cada navegación (no solo al init) para que aparezca apenas
   * el branding esté disponible, p. ej. resuelto por token en el flujo TC.
   */
  private refreshIsParams(): void {
    const brandingRaw = sessionStorage.getItem('branding');
    let branding: { delegatorId?: string; agencyId?: string } | null = null;
    try {
      branding = brandingRaw ? JSON.parse(brandingRaw) : null;
    } catch {
      branding = null;
    }
    this.isParams = !!(branding?.delegatorId && branding?.agencyId);
  }

  private buildSteps(): void {
    // El paso "Pago" existe solo en el flujo con pasarela asignada (Mercado
    // Pago o WeTravel, según la moneda de Turismo City, CAT-4903). Con TC en
    // otra moneda el stepper es el del motor normal.
    this.listSteps = buildPaymentSteps(
      this.paymentFlow.isGatewayFlow(),
      this.isMobile
    );
  }

  checkWindowWidth() {
    this.isMobile = window.innerWidth >= 1300;
  }

  onClickInactive() {
    this.idleService.setActive();
  }

  onClickErrorPrimary() {
    const currentIndex = this.listSteps.findIndex(step => step.select);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : 0;
    const prevStep = this.listSteps[prevIndex];

    if (
      this.paymentFlow.isTurismoCityFlow() &&
      (currentIndex <= 0 || prevStep?.link === '/results')
    ) {
      // En el flujo TC el paso anterior sería el listado de resultados,
      // que no existe: se lo devuelve a Turismo City.
      window.location.href = this.paymentFlow.getReturnUrl();
      return;
    }

    if (currentIndex === 0) {
      this.router.navigateByUrl('/').then(() => {
        window.location.reload();
      });
    } else {
      if (prevStep && prevStep.link) {
        this.router.navigateByUrl(prevStep.link).then(() => {
          window.location.reload();
        });
      }
    }
  }
  onClickErrorSecondary() {
    window.location.reload();
  }

  setCurrentStep(index: number) {
    this.bookingInternalStep = index;
    this.updateStepsByUrl(this.router.url);
  }

  /**
   * Reconstruye el stepper sin navegar. Lo llama booking-payments cuando
   * llega el pricing: recién ahí se conoce la moneda y, con ella, si el
   * flujo tiene paso "Pago" o no (CAT-4825).
   */
  refreshSteps(): void {
    this.buildSteps();
    this.updateStepsByUrl(this.router.url);
  }

  updateStepsByUrl(currentUrl: string) {
    const path = currentUrl.split('?')[0];
    let currentIndex = this.listSteps.findIndex(
      step => path === step.link || path.startsWith(`${step.link}/`)
    );

    // El sub-paso interno (Datos / Pago) solo existe en el flujo con
    // pasarela (Mercado Pago o WeTravel, CAT-4903).
    if (
      path.startsWith('/booking-payments') &&
      this.paymentFlow.isGatewayFlow()
    ) {
      const bookingIndex = this.listSteps.findIndex(
        step => step.link === '/booking-payments'
      );
      if (bookingIndex !== -1) {
        currentIndex = bookingIndex + (this.bookingInternalStep - 1);
      }
    }

    this.currentStepIndex = currentIndex;

    const isConfirmation = path.startsWith('/confirmation');
    const isTcFlow = this.paymentFlow.isTurismoCityFlow();
    this.listSteps = this.listSteps.map((step, index) => {
      const select = index === currentIndex;
      let disabled = index > currentIndex;

      if (isConfirmation && index < currentIndex) {
        disabled = true;
      }

      // En el flujo Turismo City no existe listado de resultados propio:
      // el paso se muestra pero nunca es navegable.
      if (isTcFlow && step.link === '/results') {
        disabled = true;
      }

      return {
        ...step,
        select,
        filled: index < currentIndex,
        disabled,
      };
    });
  }
}
